import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Card,
    CardMedia,
    CardContent,
    Alert,
    Grid
} from '@mui/material';
import { getBackendUserUrl } from '../../../utils/utils';
import axios from 'axios';
import ReadMoreText from '../../common/ReadMoreText';

const MAX_DESC_LENGTH = 150;

const VenueAutofillForm = ({ onVenueSelected }) => {
    const [venueName, setVenueName] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [address, setAddress] = useState('');
    const [contact, setContact] = useState('');
    const [previewData, setPreviewData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleAutofill = async () => {
        if (!venueName.trim()) {
            setError('Please enter a venue name');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const searchResponse = await axios.get(`${getBackendUserUrl()}/venue/search`, {
                params: {
                    query: venueName.trim()
                }
            });

            if (searchResponse.data && searchResponse.data.length > 0) {
                // The response now contains an array of { Venue, Company } objects
                const venueData = searchResponse.data[0];
                setPreviewData({
                    Venue: {
                        ...venueData.Venue,
                        title: venueData.Venue.title,
                        desc: venueData.Venue.desc,
                        coverPhoto: venueData.Venue.coverPhoto,
                        capacity: venueData.Venue.capacity,
                        features: venueData.Venue.features
                    },
                    Company: {
                        ...venueData.Company,
                        name: venueData.Company.name,
                        desc: venueData.Company.desc,
                        address: venueData.Company.address,
                        website: venueData.Company.website,
                        contact: venueData.Company.contact
                    }
                });
                return;
            }
            console.log("venueData", venueName)
            console.log("websiteUrl", websiteUrl)
            
            const response = await axios.post(getBackendUserUrl() + '/autofill_company/autofill_venue_details', {
                venue_name: venueName.trim(),
                website: websiteUrl.trim(),
                address: address.trim(),
                country: 'United States'
            });

            if (response.data) {
                setPreviewData(response.data);
            }
        } catch (err) {
            // User-friendly error message
            let userMessage = 'No venues found. Please try a more specific name.';
            if (err.response && err.response.status === 400) {
                userMessage = 'No venues found. Please try a more specific name.';
            } else if (err.response && err.response.status === 404) {
                userMessage = 'No venues found. Please check your spelling or try a different name.';
            }
            setError(userMessage);
            setPreviewData(null);
        } finally {
            setIsLoading(false);
        }
    };

    // Clear error when user types
    const handleVenueNameChange = (e) => {
        setVenueName(e.target.value);
        if (error) setError('');
    };

    const handleConfirm = () => {
        if (previewData) {
            onVenueSelected(previewData);
            setPreviewData(null);
            setVenueName('');
            setWebsiteUrl('');
            setAddress('');
            setContact('');
        }
    };

    // Helper to format address as multi-line
    const formatAddress = (address) => {
        if (!address) return null;
        return address.split(',').map((line, idx) => (
            <span key={idx} style={{ display: 'block' }}>{line.trim()}</span>
        ));
    };

    return (
        <Grid container spacing={2} marginTop={5}>
            <Grid item xs={12} md={5}>
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Already have a venue booked?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Paste the venue name and website below, and we'll autofill it for you.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth
                            label="Venue Name"
                            value={venueName}
                            onChange={handleVenueNameChange}
                            placeholder="e.g., Lume Studio, New York, NY"
                            error={!!error}
                            helperText={error}
                        />
                        <TextField
                            fullWidth
                            label="Website URL"
                            value={websiteUrl}
                            onChange={(e) => setWebsiteUrl(e.target.value)}
                            placeholder="https://..."
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth
                            label="Address"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="e.g. 123 Main St, New York, NY"
                            error={!!error}
                            helperText={error}
                        />
                        <TextField
                            fullWidth
                            label="Point of Contact"
                            value={contact}
                            onChange={(e) => setContact(e.target.value)}
                            placeholder="joe@venueSpace.com"
                        />
                    </Box>

                    <Button
                        variant="contained"
                        onClick={handleAutofill}
                        disabled={isLoading}
                        sx={{ width: '100%' }}
                    >
                        {isLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Autofill & Add Venue'
                        )}
                    </Button>
                </Box>
            </Grid>
            <Grid item xs={12} md={5}>
                {previewData && (
                    <Card sx={{ width: '100%', minWidth: 300, maxWidth: 500, mx: 'auto', p: 2, boxShadow: 3 }}>
                        {previewData.Venue?.coverPhoto ? (
                            <CardMedia
                                component="img"
                                height="180"
                                image={previewData.Venue.coverPhoto}
                                alt={previewData.Venue.title}
                                sx={{ objectFit: 'cover', borderRadius: 2, mb: 1 }}
                            />
                        ) : (
                            <Box sx={{ height: 180, bgcolor: '#f5f5f5', borderRadius: 2, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'grey.500', fontSize: 18 }}>
                                No Image Available
                            </Box>
                        )}
                        <CardContent sx={{ p: 0 }}>
                            <Typography variant="h6" gutterBottom>
                                {previewData.Venue?.title || previewData.Company?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                                {previewData.Company?.address}
                            </Typography>
                            <ReadMoreText
                                text={previewData?.Venue?.desc || previewData?.Company?.desc || ''}
                                maxLength={MAX_DESC_LENGTH}
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                            />
                            <Button 
                                variant="outlined" 
                                onClick={handleConfirm}
                                sx={{ width: '100%', mt: 2 }}
                            >
                                Add this venue to your event
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </Grid>
        </Grid>
    );
};

export default VenueAutofillForm; 